

// CartItem.js
import React, { useState, useEffect } from 'react';
import { Box, IconButton, Typography } from '@mui/material';
import {
  CartItemColumn,
  CartItemRow,
  CartItemInfo,
  CartItemMeta,
  CartItemQty,
  QtyButton,
  SmallRow
} from './styles';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

const ymdUTC = (d) => {
  if (!d) return null;
  const dt = d instanceof Date ? d : new Date(d);
  if (Number.isNaN(dt.getTime())) return null;
  return dt.toISOString().slice(0, 10); // YYYY-MM-DD
};

const isExpired = (expiryDate) => {
  const exp = ymdUTC(expiryDate);
  if (!exp) return false;
  const today = ymdUTC(new Date());
  return exp < today; // valid through expiry day
};

/**
 * item shape (current):
 * Inventory docs copied into cart, so name info is:
 * - PRODUCT: item.itemId.name
 * - MEDICINE: item.itemId.medicineId.brandName / genericName
 */
const CartItem = ({ item, onUpdateQty, onRemoveItem }) => {
  const qty = item.qty || 1;
  const maxStock = Number(item.quantity ?? 0);
  const remainingStock = Math.max(0, maxStock - qty);
  const isOutOfStock = maxStock <= 0;

  const expired = isExpired(item?.expiryDate);


  const [draftQty, setDraftQty] = useState(String(qty));

  useEffect(() => {
    setDraftQty(String(qty));
  }, [qty]);

  // ✅ NEW: unified name getter (PRODUCT + MEDICINE)
  const getDisplayName = (cartItem) => {
    const productName = (cartItem?.itemId?.name ?? '').trim(); // product
    if (productName) return productName;

    const med = cartItem?.itemId?.medicineId; // medicine
    const brand = (med?.brandName ?? '').trim();
    const generic = (med?.genericName ?? '').trim();

    const brandIsStarsOnly = brand !== '' && /^[*\s]+$/.test(brand);

    if (!brand || brandIsStarsOnly) return generic || 'Unnamed';
    return brand;
  };

  // ✅ NEW: generic line only for medicines when brand is real
  const getGenericLine = (cartItem) => {
    const med = cartItem?.itemId?.medicineId;
    const brand = (med?.brandName ?? '').trim();
    const generic = (med?.genericName ?? '').trim();
    const brandIsStarsOnly = brand !== '' && /^[*\s]+$/.test(brand);

    if (!generic) return '';
    if (!brand || brandIsStarsOnly) return '';
    return generic;
  };

  const rawUnitPrice = Number(item.retailPrice ?? 0);
  const discount = Number(item?.discount ?? 0);

  const hasDiscount = discount > 0 && item.discountApplied;

  const discountedUnitPrice = hasDiscount
    ? rawUnitPrice * (1 - discount / 100)
    : rawUnitPrice;

  const lineTotal = discountedUnitPrice * qty;

  const displayName = getDisplayName(item);
  const genericLine = getGenericLine(item);

  return (
    <CartItemColumn>
      <CartItemRow sx={{ width: '100%' }}>
        <CartItemInfo sx={{ marginRight: '15px' }}>
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 600,
              color: '#1976d2',
              textAlign: 'left',
              whiteSpace: 'normal',
              overflowWrap: 'anywhere',
              wordBreak: 'break-word',
              lineHeight: 1.2,
            }}
            noWrap
          >
            {displayName}
          </Typography>

          {genericLine && (
            <Typography
              variant="caption"
              sx={{
                color: 'grey',
                fontWeight: '600',
                textAlign: 'left',
                display: 'block',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {genericLine}
            </Typography>
          )}
        </CartItemInfo>

        <Box style={{ display: 'flex', flexDirection: 'column', width: {sm:'100%', md:'30%'}, alignItems: {sm:'flex-start', md:'flex-end'} }}>
          <CartItemMeta
            style={{
              color: '#1976d2',
              background: 'rgba(25, 118, 210, 0.1)',
              borderRadius: '3px',
              padding: '1px 7px',
            }}
          >
            {item.batchNo && (
              <Typography variant="body2" sx={{ fontWeight: '600' }}>
                {item.batchNo}
              </Typography>
            )}
          </CartItemMeta>

          <CartItemMeta>
            {typeof item.quantity === 'number' && (
              <Typography variant="body2" sx={{ fontWeight: '600', marginLeft:{xs:0, md:'auto'} }}>
                <span style={{ color: 'darkgrey' }}>Stock: </span>
                {remainingStock}
              </Typography>
            )}
          </CartItemMeta>
        </Box>
      </CartItemRow>

      <CartItemRow sx={{ width: '100%' }}>
        <CartItemInfo sx={{ marginTop: 'auto', justifyContent: 'flex-start', alignItems: 'flex-start' }}>
          {qty >= maxStock && maxStock > 0 && (
            <Typography variant="caption" sx={{ fontWeight: 600, color: '#EF5350', mt: 0.5, ml: '2px' }}>
              Stock limit reached!
            </Typography>
          )}
          {expired && (
            <Typography
              variant="caption"
              sx={{ fontWeight: 600, color: '#EF5350', mt: 0.5, ml: '2px' }}
            >
              Expired!
            </Typography>
          )}

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              marginTop: 'auto',
              gap: 0.75,
              mb: 0.5,
            }}
          >
            {qty <= 1 ? (
              <div>
                {onRemoveItem && (
                  <IconButton size="small" sx={{ border: '1px solid black' }}>
                    <DeleteOutlineIcon
                      sx={{ fontSize: '22px', color: 'black' }}
                      onClick={() => onRemoveItem(item)}
                    />
                  </IconButton>
                )}
              </div>
            ) : (
              <QtyButton
                disabled={qty <= 1 || !onUpdateQty}
                onClick={() => onUpdateQty(item, Math.max(1, qty - 1))}
              >
                −
              </QtyButton>
            )}

            <input
              className="cart-qty-input"
              type="text"
              value={draftQty}
              onChange={(e) => {
                const val = e.target.value;

                if (val === '') {
                  setDraftQty('');
                  return;
                }

                if (!/^\d+$/.test(val)) return;

                let num = Number(val);
                if (num < 1) return;

                if (maxStock > 0 && num > maxStock) {
                  num = maxStock;
                }

                setDraftQty(String(num));

                if (onUpdateQty) {
                  onUpdateQty(item, num);
                }
              }}
              onBlur={() => {
                let num = Number(draftQty);

                if (!draftQty || isNaN(num) || num < 1) {
                  num = 1;
                }

                if (maxStock > 0 && num > maxStock) {
                  num = maxStock;
                }

                setDraftQty(String(num));

                if (onUpdateQty) {
                  onUpdateQty(item, num);
                }
              }}
              style={{
                width: '35px',
                fontSize: '16px',
                textAlign: 'center',
                padding: '4px 0',
                outline: 'none',
                MozAppearance: 'textfield',
              }}
            />

            <QtyButton
              disabled={!onUpdateQty || isOutOfStock || qty >= maxStock}
              onClick={() => onUpdateQty(item, qty + 1)}
            >
              +
            </QtyButton>
          </Box>
        </CartItemInfo>

        <CartItemQty>
          <SmallRow>
            <Typography variant="body2" sx={{ fontWeight: 600, color: 'black', marginRight: '10px' }}>
              Unit Price
            </Typography>

            {hasDiscount ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, textDecoration: 'line-through', color: 'grey' }}
                >
                  Rs {rawUnitPrice.toFixed(2)}
                </Typography>
              </Box>
            ) : (
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Rs {rawUnitPrice.toFixed(2)}
              </Typography>
            )}
          </SmallRow>

          {discount > 0 && (
            <SmallRow sx={{ borderRadius: '4px' }}>
              <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={item.discountApplied}
                  onChange={(e) =>
                    onUpdateQty && onUpdateQty(item, qty, { discountApplied: e.target.checked })
                  }
                  style={{ marginRight: '8px', marginLeft: 0 }}
                />
                <Typography variant="body2" sx={{ fontWeight: 600, marginRight: 'auto', color: 'green' }}>
                  Apply {discount}% discount
                </Typography>
              </label>
            </SmallRow>
          )}

          {hasDiscount && (
            <SmallRow>
              <Typography variant="body2" sx={{ fontWeight: 600, color: 'black', marginRight: '10px' }}>
                Discounted Price
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Rs {discountedUnitPrice.toFixed(2)}
              </Typography>
            </SmallRow>
          )}

          <SmallRow style={{ marginTop: '5px', paddingTop: '5px', borderTop: '1px solid #d7d7d7', borderRadius: '0' }}>
            <Typography variant="body2" sx={{ fontWeight: 600, color: 'black', marginRight: '10px' }}>
              Total
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              Rs {lineTotal.toFixed(2)}
            </Typography>
          </SmallRow>
        </CartItemQty>
      </CartItemRow>
    </CartItemColumn>
  );
};

export default CartItem;
